/**
 * Research Accelerator surface feature module for MJExplorer.
 *
 * Declares the ONE BaseDashboard host the Explorer shell instantiates via the
 * 'AGUAppSurface' driver class and every module that can appear in a
 * composition. The host seam, surface config, module registry and data
 * services are provided by RaSurfaceComponent itself (see its `providers`),
 * not here: this module is imported eagerly into AppModule, so anything listed
 * in its `providers` is a root-injector singleton shared by every surface.
 *
 * Deliberately provides NO second root GraphQL client. MAPS already owns
 * MJExplorer's root apollo-angular client (pointed at /maps-graphql), so a
 * second one here would collide with it. Every Research Accelerator read goes
 * through MJ's own provider — RunView and BaseEntity for entity access, and
 * ResearchApiService (GraphQLDataProvider.ExecuteGQL) against
 * ResearchSearchResolver/ResearchMemberResolver on MJ's own /graphql for the
 * search/member operations. Same choice the OEP surface makes, for the same
 * reason (apps/MJExplorer/src/app/feedbackplatform/data/oep-data.service.ts).
 */
import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
    MJAlertComponent,
    MJButtonDirective,
    MJEmptyStateComponent,
    MJPageBodyComponent,
    MJPageLayoutComponent,
    MJProgressBarComponent,
    MJSwitchComponent,
} from '@memberjunction/ng-ui-components';
import { AGUAppShellModule, AGUSurfaceReadyService } from 'agu_app_shell';

import { RaSurfaceComponent, LoadRaSurface } from './ra-surface.component';

import { RaDashboardComponent } from './modules/dashboard/ra-dashboard.component';
import { GrantFinderPageComponent } from './modules/grant-finder/grant-finder-page.component';
import { GrantCardComponent } from './modules/grant-finder/grant-card.component';
import { GrantFiltersComponent } from './modules/grant-finder/grant-filters.component';
import { GrantDetailComponent } from './modules/grant-finder/grant-detail.component';
import { PeopleFinderPageComponent } from './modules/people-finder/people-finder-page.component';
import { PersonCardComponent } from './modules/people-finder/person-card.component';
import { PersonDetailComponent } from './modules/people-finder/person-detail.component';
import { PeopleFiltersComponent } from './modules/people-finder/people-filters.component';
import { JobFinderPageComponent } from './modules/job-finder/job-finder-page.component';
import { JobCardComponent } from './modules/job-finder/job-card.component';
import { JobFiltersComponent } from './modules/job-finder/job-filters.component';
import { JobDetailComponent } from './modules/job-finder/job-detail.component';
import { RaFinderToolbarComponent } from './finder/ra-finder-toolbar.component';
import { RaActivePillsComponent } from './finder/ra-active-pills.component';
import { RaPressedDirective } from './finder/ra-pressed.directive';
import { SavedPageComponent } from './modules/saved/saved-page.component';
import { RaPersonProfileComponent } from './modules/profile/ra-person-profile.component';
import { MyProfilePageComponent } from './modules/profile/my-profile-page.component';
import { SettingsPageComponent } from './modules/settings/settings-page.component';
import { SearchPageComponent } from './modules/search/search-page.component';
import { CareerExplorerPageComponent } from './modules/career-explorer/career-explorer-page.component';
import { CxStartComponent } from './modules/career-explorer/cx-start.component';
import { CxAboutDialogComponent } from './modules/career-explorer/cx-about-dialog.component';
import { CxReviewComponent } from './modules/career-explorer/cx-review.component';
import { CxPdfViewerComponent } from './modules/career-explorer/cx-pdf-viewer.component';
import { CxReadyComponent } from './modules/career-explorer/cx-ready.component';
import { CxRoleCardComponent } from './modules/career-explorer/cx-role-card.component';
import { CxRingComponent } from './modules/career-explorer/cx-ring.component';
import { CxPathsComponent } from './modules/career-explorer/cx-paths.component';
import { CxRoleComponent } from './modules/career-explorer/cx-role.component';

@NgModule({
    declarations: [
        RaSurfaceComponent,
        RaDashboardComponent,
        GrantFinderPageComponent,
        GrantCardComponent,
        GrantFiltersComponent,
        GrantDetailComponent,
        PeopleFinderPageComponent,
        PersonCardComponent,
        PersonDetailComponent,
        PeopleFiltersComponent,
        JobFinderPageComponent,
        JobCardComponent,
        JobFiltersComponent,
        JobDetailComponent,
        RaFinderToolbarComponent,
        RaActivePillsComponent,
        RaPressedDirective,
        SavedPageComponent,
        RaPersonProfileComponent,
        MyProfilePageComponent,
        SettingsPageComponent,
        SearchPageComponent,
        CareerExplorerPageComponent,
        CxStartComponent,
        CxAboutDialogComponent,
        CxReviewComponent,
        CxPdfViewerComponent,
        CxReadyComponent,
        CxRoleCardComponent,
        CxRingComponent,
        CxPathsComponent,
        CxRoleComponent,
    ],
    imports: [
        CommonModule,
        FormsModule,
        // The shared app shell: masthead, module nav, search hero.
        AGUAppShellModule,
        // MJ canonical chrome. The header, tab-nav, page-search, filter-popover
        // and stat-badge components went with Task 6: every screen now opens on
        // `agu-plain-header` and tabs/sorts/searches through the portal's own
        // `agu-tab-strip` / `agu-sort-trigger` / native mockup fields, so
        // importing MJ's versions here only kept dead chrome in the bundle.
        MJAlertComponent,
        MJButtonDirective,
        MJEmptyStateComponent,
        MJPageBodyComponent,
        MJPageLayoutComponent,
        MJProgressBarComponent,
        MJSwitchComponent,
    ],
    // Only the readiness gate stays here. NgModule providers of an eagerly imported module land in
    // the ONE root injector (AppModule imports this module directly), and the gate is the one service
    // that is genuinely app-wide: it wraps MJ's global StartupManager, so a single shared instance is
    // correct. Everything with per-surface state or config is provided by RaSurfaceComponent.
    providers: [AGUSurfaceReadyService],
})
export class RaSurfaceModule {
    constructor() {
        LoadRaSurface();
    }
}
